import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCollegeDetQuery } from '../../store/api/userApiSlice';
import FilterPanel from './FilterPanel';
import './CollegeSearchResults.css';
import { IoSearch, IoLocationOutline, IoGlobeOutline, IoChevronDown } from 'react-icons/io5';
import { FaStar } from 'react-icons/fa';
import { MdKeyboardDoubleArrowUp } from "react-icons/md";
import { HiOutlineCurrencyRupee } from "react-icons/hi";
import collegePlaceholder from '../../assets/images/ftrdClgImgPlaceholder.png';
import CustomDropdown from './CustomDropdown';

const CollegeSearchResults = () => {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  // Fetch data
  // Fetch data
  const { data, isLoading, error } = useCollegeDetQuery();
  const institutes = data?.data || data?.colleges || data?.institute || (Array.isArray(data) ? data : []) || [];

  // Local state for implementation consistent with API limitations
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [sortBy, setSortBy] = useState('Popularity');
  const [displayCount, setDisplayCount] = useState(10); // Simple pagination
  const [activeFilters, setActiveFilters] = useState({}); // Stores filters applied from panel

  // Sync local state with URL param if it changes
  useEffect(() => {
    const queryFromUrl = searchParams.get('q') || '';
    setSearchQuery(queryFromUrl);
  }, [searchParams]);

  // Filter institutes locally based on search query AND active filters
  const filteredInstitutes = institutes.filter(inst => {
    // 1. Text Search Filter
    let matchesSearch = true;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const nameMatch = inst.name?.toLowerCase().includes(query) || false;
      const townMatch = (inst.town || inst.city || inst.district || "").toLowerCase().includes(query) || false;
      const stateMatch = inst.state?.toLowerCase().includes(query) || false;
      matchesSearch = nameMatch || townMatch || stateMatch;

      // Course/Program Filtering Logic
      if (!matchesSearch) {
        // Helper to safely check array or string
        const checkField = (field) => {
          if (!field) return false;
          if (Array.isArray(field)) {
             return field.some(item => 
               (typeof item === 'string' && item.toLowerCase().includes(query)) ||
               (item.name && item.name.toLowerCase().includes(query)) ||
               (item.courseName && item.courseName.toLowerCase().includes(query))
             );
          }
          if (typeof field === 'string') {
            return field.toLowerCase().includes(query);
          }
          return false;
        };

        const courseMatch = checkField(inst.courses) || 
                            checkField(inst.course) || 
                            checkField(inst.program) || 
                            checkField(inst.programs) ||
                            checkField(inst.type) ||
                            checkField(inst.category) ||
                            (inst.cType?.name && inst.cType.name.toLowerCase().includes(query));
        
        matchesSearch = courseMatch;
      }
    }

    // 2. Panel Filters
    let matchesFilters = true;

    // State Filter
    if (activeFilters.state) {
      if (!inst.state?.toLowerCase().includes(activeFilters.state.toLowerCase())) {
        matchesFilters = false;
      }
    }

    // District Filter
    if (matchesFilters && activeFilters.districts && activeFilters.districts.length > 0) {
      const locationName = (inst.town || inst.city || inst.district || "").toLowerCase();
      const districtMatch = activeFilters.districts.some(district => 
        locationName === district.toLowerCase()
      );
      if (!districtMatch) {
         matchesFilters = false;
      }
    }

    // Course Type Filter
    if (matchesFilters && activeFilters.courseType) {
        const selectedType = activeFilters.courseType.toLowerCase();
        const hasCType = inst.cType?.name?.toLowerCase().includes(selectedType);
        // Fallback check in courses array if cType missing
        const hasCourseArray = Array.isArray(inst.courses) && inst.courses.some(c => 
            (typeof c === 'string' ? c : c.name).toLowerCase().includes(selectedType)
        );
        
        if (!hasCType && !hasCourseArray) {
            matchesFilters = false;
        }
    }

    // NIRF Ranking Filter
    if (matchesFilters && activeFilters.ranking) {
        const rankStr = (inst.ranking || inst.nirf_ranking || "").toString().toLowerCase();
        // Extract first number found. e.g. "#101-125" -> 101. "5" -> 5.
        const rankMatch = rankStr.match(/(\d+)/);
        const rank = rankMatch ? parseInt(rankMatch[0], 10) : null;

        if (rank === null) {
             matchesFilters = false; // Filter out if no ranking data available
        } else {
            const range = activeFilters.ranking;
            if (range === "1-10" && !(rank >= 1 && rank <= 10)) matchesFilters = false;
            else if (range === "11-50" && !(rank >= 11 && rank <= 50)) matchesFilters = false;
            else if (range === "51-100" && !(rank >= 51 && rank <= 100)) matchesFilters = false;
            else if (range === "100+" && !(rank > 100)) matchesFilters = false;
        }
    }

    // Fees Filter
    if (matchesFilters && activeFilters.fees) {
        // Parse fee. Handle "43.95 L" or "₹1.7 L"
        const feeStr = (inst.totalFee || inst.fees || inst.totalFees || "").toString();
        let amount = 0;
        
        // Simple parser: find first number (float allowed)
        const numMatch = feeStr.match(/([\d.]+)/);
        if (numMatch) {
            amount = parseFloat(numMatch[0]);
            // Check for 'L' or 'Lakh' indicating Lakhs
            if (feeStr.toLowerCase().includes('l')) {
                amount = amount * 100000;
            } else if (!feeStr.toLowerCase().includes('k') && amount < 1000) {
                 // Assume plain number implies lakhs if very small? Or raw rupees.
                 // Dangerous assumption, sticking to explicit 'L' check or raw parsing.
                 // If raw number > 1000, treat as rupees. If < 100 and no 'L', might be ambiguous, but usually 'L'.
                 // For safety strictly check 'L' for multiplier.
            }
        }
        
        // Also consider lowFee/highFee if totalFee missing
        if (amount === 0 && inst.lowFee) {
             amount = inst.lowFee; // Use lower bound for filtering "Below X"
        }

        if (amount > 0) {
            const range = activeFilters.fees;
            if (range === "Below ₹1,00,000" && amount >= 100000) matchesFilters = false;
            else if (range === "₹1,00,000 - ₹3,00,000" && (amount < 100000 || amount > 300000)) matchesFilters = false;
            else if (range === "₹3,00,000 - ₹10,00,000" && (amount < 300000 || amount > 1000000)) matchesFilters = false;
            else if (range === "₹10,00,000+" && amount < 1000000) matchesFilters = false;
        } else {
             // specific fee filter active but no fee data -> exclude or include?
             // Usually exclude.
             matchesFilters = false;
        }
    }
    
    return matchesSearch && matchesFilters;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is reactive already
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    setDisplayCount(10); // Reset pagination on filter change
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 10);
  };

  const formatFees = (fees) => {
    // API doesn't return fees, using placeholder logic if needed
    if (!fees) return 'Fees N/A';
    if (fees >= 100000) {
      return `₹${(fees / 100000).toFixed(1)} Lakhs`;
    }
    return `₹${fees.toLocaleString()}`;
  };

  return (
    <div className="search-results-container">
      {/* Left Sidebar - Filters */}
      <div className="search-sidebar">
        <FilterPanel onApply={handleApplyFilters} />
      </div>

      {/* Right Content - Results */}
      <div className="search-content">
        
        {/* Search Header */}
        <div className="search-results-header-container">
          <h2 className="search-results-title">
            Search results {filteredInstitutes.length > 0 && `(${filteredInstitutes.length})`}
          </h2>
          <div className="search-controls-wrapper">
            <form onSubmit={handleSearch} className="search-bar-wrapper">
              <IoSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search colleges by name" 
                className="college-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
            
            <div className="sort-wrapper">
              <div style={{ width: '180px' }}>
                <CustomDropdown 
                  options={['Popularity', 'Rating', 'Fees: Low to High']}
                  value={sortBy}
                  onChange={setSortBy}
                  placeholder="SORT BY"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Searching colleges...</p>
          </div>
        )}

        {/* No Results */}
        {!isLoading && filteredInstitutes.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No colleges found. Try adjusting your search.</p>
          </div>
        )}

        {/* Results List */}
        {!isLoading && filteredInstitutes.length > 0 && (
          <>
            <div className="results-list">
              {filteredInstitutes.slice(0, displayCount).map((college) => (
                <div key={college._id} className="result-card">
                  <div className="result-image-wrapper">
                    <img src={collegePlaceholder} alt={college.name} className="result-image" />
                  </div>
                  
                  <div className="result-info">
                    <div className="result-header">
                      <h3 className="result-name">{college.name}</h3>
                      <span className="result-ranking">NIRF Rank {college.ranking || college.nirf_ranking || 'N/A'}</span>
                    </div>
                    
                    <p className="result-description">
                      {college.description || `${college.name} is a ${college.authority || 'premier'} ${college.cType?.name || 'educational'} institute in ${college.city || college.town || "City"}, ${college.state}.${college.examAccepted ? ` Accepts ${college.examAccepted}.` : ''}`}
                    </p>
                    
                    <div className="result-meta">
                      <div className="meta-item">
                        <IoLocationOutline /> {college.town || college.city || college.district || "City"}, {college.state}
                      </div>
                      <div className="meta-item">
                        <HiOutlineCurrencyRupee /> {college.totalFee || college.fees || college.totalFees || (college.lowFee ? `${formatFees(college.lowFee)} - ${formatFees(college.highFee)}` : 'Fees N/A')}
                      </div>
                      <div className="meta-item rating">
                        {college.rating || 4.5} <div className="stars"><FaStar /><FaStar /><FaStar /><FaStar /><FaStar /></div>
                      </div>
                    </div>
                    
                    <a 
                      href={college.website || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="official-website-link"
                      onClick={(e) => !college.website && e.preventDefault()}
                    >
                      Open Official website <IoGlobeOutline className="globe-icon" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="search-footer-actions">
              {displayCount < filteredInstitutes.length && (
                <button 
                  className="load-more-btn" 
                  onClick={handleLoadMore}
                >
                  LOAD MORE <IoChevronDown />
                </button>
              )}
              <button className="back-to-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                BACK TO TOP <MdKeyboardDoubleArrowUp />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CollegeSearchResults;
